import * as Sentry from '@sentry/react-native';
import { useEffect, useState } from 'react';

import { asApiError, PSErrorClass } from '@/src/api/apiError';
import authApi, { type UserMembership } from '@/src/api/authApi';
import membershipsApi from '@/src/api/membershipsApi';
import { setRestoredUser } from '@/src/lib/sessionStore';

/**
 * `GET /me/memberships/{id}` until the membership stops being `pending` — which
 * is what a purchase waits on: nothing the client does confirms it, the webhook
 * Razorpay sends OUR server does, and that can land before or after the user is
 * back in the app.
 *
 * Settling means it stopped MOVING, not that it went live. The screen reads
 * `membership.status`. Retry rules follow [[usePollBooking]]: a 4xx is a verdict
 * and settles at once, anything transient is a tick that didn't land, and
 * `MAX_ATTEMPTS` bounds the wait.
 */

const POLL_INTERVAL_MS = 2000;
/** 30 × 2s ≈ a minute of waiting. */
const MAX_ATTEMPTS = 30;

export const TIMEOUT_MESSAGE =
  "This is taking longer than usual to confirm. Your membership will show up on its own once the payment is through — there's no need to pay again.";

export type PollMembershipState =
  | { status: 'loading'; membership: null; error: null }
  | { status: 'error'; membership: null; error: PSErrorClass }
  | { status: 'success'; membership: UserMembership; error: null };

const LOADING: PollMembershipState = {
  status: 'loading',
  membership: null,
  error: null,
};

export function usePollMembership(id: string): PollMembershipState {
  const [state, setState] = useState<PollMembershipState>(LOADING);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    // See [[usePollBooking]]: split from `attempts` so a timeout distinguishes a
    // capture that never landed from a run of failed requests.
    let failedTicks = 0;

    const ask = async () => {
      attempts += 1;

      try {
        const membership = await membershipsApi.getMyMembership(id);
        if (!active) return;

        if (membership.status !== 'pending') {
          setState({ status: 'success', membership, error: null });
          refreshSessionUser();
          return;
        }
        // Still pending — fall through and schedule the next ask.
      } catch (caught) {
        if (!active) return;

        const error = asApiError(caught);
        if (error.kind === 'api') {
          setState({ status: 'error', membership: null, error });
          return;
        }
        failedTicks += 1;
      }

      if (attempts >= MAX_ATTEMPTS) {
        // Money has moved and the membership still isn't live — see
        // [[usePollBooking]] on why the rate of this is the only signal.
        Sentry.captureMessage('Membership poll timed out', {
          level: 'warning',
          extra: { membershipId: id, attempts, failedTicks },
        });
        setState({
          status: 'error',
          membership: null,
          error: new PSErrorClass({
            kind: 'unknown',
            message: TIMEOUT_MESSAGE,
          }),
        });
        return;
      }

      timer = setTimeout(ask, POLL_INTERVAL_MS);
    };

    ask();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [id]);

  return state;
}

/**
 * Re-read the user, because their `membership` block is now stale and every gate
 * in the app reads it — home would go on selling the plan they just bought.
 *
 * Deliberately not awaited and deliberately not able to fail the poll: the
 * membership settled either way, and it's the store that's behind, not the
 * purchase. Left running when the screen unmounts too — the write is to a module
 * store, not to component state.
 */
async function refreshSessionUser() {
  try {
    setRestoredUser(await authApi.fetchMe());
  } catch (caught) {
    console.debug('Session refresh after membership purchase failed', caught);
  }
}
